import type { SlideCanvasBox, SlideRenderBox } from '../components/SlideCanvas.types';
import { ROTATION_HANDLE_OFFSET, ROTATION_HANDLE_RADIUS } from './slideRotation';
import { RESIZE_HANDLE_VISUAL_RADIUS } from './slideInteraction';

export type SlideRendererParams = {
  background: string;
  backgroundImage: CanvasImageSource | null;
  renderBoxes: SlideRenderBox[];
  selectedBoxId: string | null;
  selectedStroke: string;
  selectedFill: string;
  stroke: string;
  fill: string;
  draftBox: SlideCanvasBox | null;
  draftStroke: string;
  draftFill: string;
  width: number;
  height: number;
  resolveColor: (label: string) => { stroke: string; fill: string };
};

export type SlideRenderer = {
  sync(canvas: HTMLCanvasElement | null, width: number, height: number): void;
  draw(params: SlideRendererParams): void;
};

export function createLinearRenderer(): SlideRenderer {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let deviceRatio = 1;

  function sync(target: HTMLCanvasElement | null, width: number, height: number) {
    canvas = target;
    if (!canvas) {
      ctx = null;
      return;
    }
    deviceRatio = window.devicePixelRatio || 1;
    canvas.width = width * deviceRatio;
    canvas.height = height * deviceRatio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx = canvas.getContext('2d');
  }

  function draw(params: SlideRendererParams) {
    const context = ctx;
    if (!canvas || !context) return;
    context.save();
    context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);
    context.clearRect(0, 0, params.width, params.height);
    context.fillStyle = params.background;
    context.fillRect(0, 0, params.width, params.height);
    
    let scaleX = 1;
    let scaleY = 1;
    if (params.backgroundImage) {
      context.drawImage(params.backgroundImage, 0, 0, params.width, params.height);
      if ('naturalWidth' in params.backgroundImage) {
        const img = params.backgroundImage as HTMLImageElement;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          scaleX = params.width / img.naturalWidth;
          scaleY = params.height / img.naturalHeight;
        }
      }
    }

    params.renderBoxes.forEach((box) => {
      const palette = params.resolveColor(box.label) ?? { stroke: params.stroke, fill: params.fill };
      const isSelected = params.selectedBoxId === box.id;
      const strokeColor = isSelected ? params.selectedStroke : palette.stroke ?? params.stroke;
      const fillColor = isSelected ? params.selectedFill : palette.fill ?? params.fill;
      paintRect(context, box, strokeColor, fillColor, scaleX, scaleY);
      if (isSelected) {
        paintHandle(context, box, strokeColor, scaleX, scaleY);
      }
    });

    if (params.draftBox) {
      paintRect(context, params.draftBox, params.draftStroke, params.draftFill, scaleX, scaleY, true);
    }
    context.restore();
  }

  function paintRect(
    ctx: CanvasRenderingContext2D,
    box: SlideCanvasBox,
    strokeColor: string,
    fillColor: string,
    scaleX: number,
    scaleY: number,
    dashed = false,
  ) {
    ctx.save();
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    if (dashed) {
      ctx.setLineDash([6, 4]);
    }
    const { cx, cy, width, height } = getScaledRect(box, scaleX, scaleY);
    const rotationRad = (box.rotation * Math.PI) / 180;
    ctx.translate(cx, cy);
    ctx.rotate(rotationRad);
    ctx.fillRect(-width / 2, -height / 2, width, height);
    ctx.strokeRect(-width / 2, -height / 2, width, height);
    ctx.restore();
  }

  function paintHandle(
    ctx: CanvasRenderingContext2D,
    box: SlideCanvasBox,
    strokeColor: string,
    scaleX: number,
    scaleY: number,
  ) {
    const { cx, cy, width, height } = getScaledRect(box, scaleX, scaleY);
    const rotationRad = (box.rotation * Math.PI) / 180;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotationRad);
    
    paintRotationHandle(ctx, height, strokeColor);
    paintResizeHandles(ctx, width, height, strokeColor);

    ctx.restore();
  }

  function paintRotationHandle(
    ctx: CanvasRenderingContext2D,
    height: number,
    strokeColor: string,
  ) {
    ctx.fillStyle = strokeColor;
    ctx.beginPath();
    ctx.arc(
      0,
      -height / 2 - ROTATION_HANDLE_OFFSET,
      ROTATION_HANDLE_RADIUS,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  function paintResizeHandles(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    strokeColor: string,
  ) {
    const hw = width / 2;
    const hh = height / 2;
    const corners = [
      { x: -hw, y: -hh }, // NW
      { x: hw, y: -hh },  // NE
      { x: hw, y: hh },   // SE
      { x: -hw, y: hh },  // SW
    ];

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1;

    corners.forEach((c) => {
      ctx.beginPath();
      ctx.arc(c.x, c.y, RESIZE_HANDLE_VISUAL_RADIUS, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });
  }

  function getScaledRect(box: SlideCanvasBox, scaleX: number, scaleY: number) {
    const cx = (box.x + box.width / 2) * scaleX;
    const cy = (box.y + box.height / 2) * scaleY;
    return {
      cx,
      cy,
      width: box.width * scaleX,
      height: box.height * scaleY,
    };
  }

  return { sync, draw };
}
